import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Settings } from 'lucide-react';
import { Container, PageHeader, Section, SettingRow } from '@vnetwork/ui/layout';
import { Badge, Button, Skeleton } from '@vnetwork/ui/primitives';
import { Alert } from '@vnetwork/ui/feedback';
import { useMyServices, serviceLabel } from '@/api/services';
import { useClusters } from '@/api/k8s/clusters';
import { KubernetesRenameServiceDialog } from './KubernetesRenameServiceDialog';
import { KubernetesCancelSubscriptionDialog } from './KubernetesCancelSubscriptionDialog';
import { KubernetesDeleteServiceDialog } from './KubernetesDeleteServiceDialog';

/**
 * Settings page for a scoped Kubernetes console (service scope, not cluster scope).
 * Exposes: rename (General), cancel subscription + delete service (Danger zone).
 * Danger-zone actions are blocked while clusters exist.
 */
export function KubernetesServiceSettings() {
  const { serviceId } = useParams<{ serviceId: string }>();
  const [renameOpen, setRenameOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const { data: services = [], isLoading: servicesLoading } = useMyServices();
  const { data: clusters = [], isLoading: clustersLoading } = useClusters(serviceId);

  const service = useMemo(
    () => services.find((s) => s.id === serviceId),
    [services, serviceId],
  );

  const serviceName = service ? serviceLabel(service) : '';
  const clusterCount = clusters.length;
  const isLoading = servicesLoading || clustersLoading;
  const blocked = clusterCount > 0;

  const nameControl = isLoading ? (
    <Skeleton style={{ height: 28, width: 160 }} />
  ) : (
    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
      <Badge tone="neutral" appearance="subtle">{serviceName || '—'}</Badge>
      <Button variant="secondary" size="sm" disabled={!service} onClick={() => setRenameOpen(true)}>
        Rename
      </Button>
    </div>
  );

  const blockedAlert = blocked ? (
    <Alert tone="warning" title={`Blocked: ${clusterCount} cluster${clusterCount === 1 ? '' : 's'} still active`}>
      Delete all clusters before cancelling or deleting this service.
    </Alert>
  ) : null;

  return (
    <Container size="xl">
      <PageHeader
        icon={<Settings size={22} />}
        title="Settings"
        description="Manage this Kubernetes service."
      />

      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
        <Section
          title="General"
          description="Basic details for this Kubernetes service."
          style={{ marginBottom: 0 }}
        >
          <SettingRow
            title="Service name"
            description="The display name shown across the console and service list."
            control={nameControl}
          />
        </Section>

        <Section
          title="Danger zone"
          description="Destructive actions for this Kubernetes service."
          style={{ marginBottom: 0 }}
        >
          <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
          {blockedAlert}

          <SettingRow
            title="Cancel subscription"
            description={
              blocked
                ? `Blocked: ${clusterCount} cluster${clusterCount === 1 ? '' : 's'} still active — delete them first.`
                : 'Archives the billing subscription and marks this service as cancelled.'
            }
            control={
              <Button
                variant="danger"
                size="sm"
                disabled={!service || blocked}
                onClick={() => setCancelOpen(true)}
              >
                Cancel subscription
              </Button>
            }
          />

          <SettingRow
            title="Delete this service"
            description={
              blocked
                ? `Blocked: ${clusterCount} cluster${clusterCount === 1 ? '' : 's'} still active — delete them first.`
                : 'Removes the Kubernetes service and its zone bindings. Recorded in the activity log.'
            }
            control={
              <Button
                variant="danger"
                size="sm"
                disabled={!service || blocked}
                onClick={() => setDeleteOpen(true)}
              >
                Delete service
              </Button>
            }
          />
          </div>
        </Section>
      </div>

      {service && (
        <>
          <KubernetesRenameServiceDialog
            open={renameOpen}
            onOpenChange={setRenameOpen}
            serviceId={serviceId!}
            currentName={serviceName}
          />
          <KubernetesCancelSubscriptionDialog
            open={cancelOpen}
            onOpenChange={setCancelOpen}
            serviceId={serviceId!}
            serviceName={serviceName}
          />
          <KubernetesDeleteServiceDialog
            open={deleteOpen}
            onOpenChange={setDeleteOpen}
            serviceId={serviceId!}
            serviceName={serviceName}
            clusterCount={clusterCount}
          />
        </>
      )}
    </Container>
  );
}
